/**
 * CollectionMonitorSummary - The collection overview's header summary
 *
 * Atomic level: Organism
 * The dot, the name and the source line, then the pause line and the health line, in
 * the words of the shared map in `collection.ts`.
 */

import React from 'react';
import { MonitoringDot } from './components/MonitoringDot';
import {
  MONITORING_PAUSED_COPY,
  monitorHealthCopy,
  type Collection,
  type UnhealthyStatus,
} from '../../../services/types/collection';

// The backend sends naive UTC date-times with no offset, which `Date` would read as local.
const OFFSET_LESS_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

export const formatRelativeTime = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  const date = new Date(OFFSET_LESS_DATE_TIME.test(dateString) ? `${dateString}Z` : dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

// Duplicated from RecordsPage's header: small and stable. If a third surface needs the
// same maps, lift them into a shared module.
const SKILL_NAMES: Record<string, string> = {
  'intercom-reporting': 'Intercom',
  'dataeq-analyse': 'DataEQ',
};
const CADENCE_LABELS: Record<string, string> = {
  every_30m: 'Every 30 min',
  every_1h: 'Every hour',
  every_3h: 'Every 3 hours',
  every_6h: 'Every 6 hours',
  every_12h: 'Every 12 hours',
  every_24h: 'Daily',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

const TONE_TEXT: Record<UnhealthyStatus, string> = {
  warning: 'text-amber-700',
  critical: 'text-[var(--go-berry)]',
};

const summaryLine = (collection: Collection): string => {
  const source = collection.source;
  const sourcePart = source
    ? `${SKILL_NAMES[source.skillName] || source.skillName} · ${CADENCE_LABELS[source.cadence] || source.cadence}`
    : collection.type;
  // A source whose first tick has not run has no lastRunAt: it reads "Never" rather than
  // the collection's own update time, which only a manual collection falls back to.
  let lastSync: string;
  if (source) {
    lastSync = source.lastRunAt ? formatRelativeTime(source.lastRunAt) : 'Never';
  } else {
    lastSync = formatRelativeTime(collection.updatedAt);
  }
  return `${sourcePart} · Last synced ${lastSync} · ${(collection.recordCount ?? 0).toLocaleString()} records`;
};

interface CollectionMonitorSummaryProps {
  collection: Collection;
}

export const CollectionMonitorSummary: React.FC<CollectionMonitorSummaryProps> = ({ collection }) => {
  const source = collection.source;
  const health = monitorHealthCopy(source?.healthStatus, source?.healthReason);
  const healthLine = health && [
    health.hint ? `${health.label} — ${health.hint}` : health.label,
    ...(source?.healthSince ? [`since ${formatRelativeTime(source.healthSince)}`] : []),
  ].join(' · ');

  return (
    <div>
      <div className="flex items-center gap-2">
        <MonitoringDot
          status={collection.monitoringStatus ?? 'active'}
          tone={health?.status}
          title={health?.label}
        />
        <h1 className="text-[22px] font-extrabold tracking-tight">{collection.name}</h1>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">{summaryLine(collection)}</p>
      {collection.monitoringStatus === 'paused' && (
        <p className="text-xs text-muted-foreground mt-0.5">
          {`${MONITORING_PAUSED_COPY.label} — ${MONITORING_PAUSED_COPY.hint}`}
        </p>
      )}
      {health && <p className={`text-xs mt-0.5 ${TONE_TEXT[health.status]}`}>{healthLine}</p>}
    </div>
  );
};
